import supabase from '../../supabase/supabase';

/**
 * AUTH API - Optimized for Magic Link Auto-Joining
 */

export const getActiveMember = async (userId) => {
  const { data, error } = await supabase
    .from('members_20240320')
    .select('household_id, role, last_active_at')
    .eq('user_id', userId)
    .order('last_active_at', { ascending: false })
    .limit(1);

  if (error) return null;
  return data && data.length > 0 ? data[0] : null;
};

export const joinHouseholdWithToken = async (userId, email, inviteToken) => {
  // 1. Find invite
  const { data: invite, error: invError } = await supabase
    .from('invites_20240320')
    .select('*')
    .eq('token', inviteToken)
    .single();

  if (invError || !invite) throw new Error("Invite link invalid or expired.");

  // 2. Check if already a member
  const { data: existing } = await supabase
    .from('members_20240320')
    .select('household_id, role')
    .eq('user_id', userId)
    .eq('household_id', invite.household_id)
    .single();

  if (existing) {
    // Just update the timestamp to make it active
    await supabase
      .from('members_20240320')
      .update({ 
        last_active_at: new Date().toISOString(),
        email: email // Keep email synced
      })
      .eq('user_id', userId)
      .eq('household_id', invite.household_id);
      
    return { householdId: existing.household_id, role: existing.role };
  }

  // 3. Join as new member
  const { error: joinErr } = await supabase.from('members_20240320').insert({
    user_id: userId,
    household_id: invite.household_id,
    role: 'member',
    email: email,
    last_active_at: new Date().toISOString()
  });

  if (joinErr) throw joinErr;

  // 4. Mark used (Optional: Keep it usable for families, but mark as used for audit)
  // We'll leave it usable so multiple parents can use the same link if desired, 
  // or mark it used if we want strictly single-use.
  // For family apps, multi-use is often easier.
  // await supabase.from('invites_20240320').update({ is_used: true }).eq('id', invite.id);

  return { householdId: invite.household_id, role: 'member' };
};

export const login = async (email, password, inviteToken) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;

  let householdId, role;

  if (inviteToken) {
    try {
      const joinData = await joinHouseholdWithToken(data.user.id, email, inviteToken);
      householdId = joinData.householdId;
      role = joinData.role;
    } catch (e) {
      console.warn("Auto-join on login failed:", e.message);
    }
  }

  if (!householdId) {
    const member = await getActiveMember(data.user.id);
    householdId = member?.household_id;
    role = member?.role;
  }

  // Fallback
  if (!householdId) {
    const { data: h } = await supabase.from('households_20240320').insert({ name: 'My Family' }).select().single();
    await supabase.from('members_20240320').insert({ 
      user_id: data.user.id, 
      household_id: h.id, 
      role: 'admin', 
      email 
    });
    householdId = h.id;
    role = 'admin';
  }

  return { ...data.user, householdId, role };
};

export const register = async (email, password, inviteToken) => {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error && error.message.includes('already registered')) return login(email, password, inviteToken);
  if (error) throw error;
  return login(email, password, inviteToken);
};

export const getSession = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  const member = await getActiveMember(session.user.id);
  return { ...session.user, householdId: member?.household_id, role: member?.role };
};

export const logout = async () => {
  await supabase.auth.signOut();
};

export const updateProfile = async ({ email, password }) => {
  const { data, error } = await supabase.auth.updateUser({ email, password });
  if (error) throw error;
  return data.user;
};